import React, { useState, ChangeEvent, useEffect } from 'react';
import { TreeView } from '@material-ui/lab';
import MinusSquare from '../../icons/minus-square';
import PlusSquare from '../../icons/plus-square';
import { AnnoNode, EditorialType, Annotation, UserRole, User, AnnoNodeTypeName } from "../../domain/domain";
import { Grid, makeStyles, Menu, MenuItem } from '@material-ui/core';
import { ApplicationState } from '../../reducers';
import { useDispatch, useSelector } from 'react-redux';
import AnnotationTreeItemComponent from './AnnotationTreeItemComponent';
import { getAnnoNodeTypeNameText, getLevelsHeirarchy, newAddedChildrenData } from './utils';
import { ComponentManagementActionType } from '../../reducers/comp.actions';
import { UiActionType } from '../../reducers/ui.reducer';
import { ConfirmationDialog } from '../Dialogs/ConfirmationDialog';
import { saveEditorialContent, deleteAnnotationNodeById } from '../../api/node';

type Props = {
  root: Annotation,
  selectedNodeId: number | undefined,
  onNodeClicked: (node: AnnoNode) => void,
};

const useStyles = makeStyles(_ => ({
  deleteMenuItem: {
    color: 'red'
  },
  repealMenuItem: {
    color: 'red',
  },
  printMenuItem: {

  },
  root: {
    overflowY: 'auto',
    height: '90vh',
    maxHeight: '90vh',
    display: 'block'
  }
}));

const AnnotationTreeView: React.FC<Props> = ({ root, selectedNodeId, onNodeClicked }) => {
  let currentUser: User | undefined = useSelector((app: ApplicationState) => app.user.currentUser);
  const dispatch = useDispatch();
  const [contextMenuOpen, setContextMenuOpen] = useState<boolean>(false);
  const [openAddSiblingDialog, setOpenAddSiblingDialog] = useState<boolean>(false);
  const [openRemoveNodeAndItsChildrenDialog, setOpenRemoveNodeAndItsChildrenDialog] = useState<boolean>(false);
  const [currentAnnoNode, setCurrentAnnoNode] = useState<AnnoNode | undefined>(undefined);
  const [currentClickedAnnoNode, setCurrentClickedAnnoNode] = useState<AnnoNode | undefined>(undefined);
  const [currentAnnoNodeType, setCurrentAnnoNodeType] = useState<AnnoNodeTypeName | undefined>(undefined);
  const [direction, setDirection] = useState<string>('');
  const [currentAnnoNodeOrdinal, setCurrentAnnoNodeOrdinal] = useState<number>(0);
  const [posX, setPosX] = useState<number | undefined>(undefined);
  const [posY, setPosY] = useState<number | undefined>(undefined);
  const [contextMenuSubTree, setContextMenuSubTree] = useState<AnnoNode | undefined>(undefined);
  const [openSwitchDialogue, setOpenSwitchDialogue] = useState<boolean>(false);
  const [expandedAnnoNodes, setExpandedAnnoNodes] = useState<number[]>([0]);
  const [toggledNodes, setToggledNodes] = useState<number[]>([0]);
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState<boolean>(false);

  const classes = useStyles();
  const editorNode = useSelector((app: ApplicationState) => app.comp.editorNode);
  const currentEditorTextForAnnotation = useSelector((app: ApplicationState) => app.comp.currentEditorTextForAnnotation);
  const editorialEditorStates = useSelector((state: ApplicationState) => state.comp.editorialEditorStates);

  const findAnnoNodeById = (nodeList: AnnoNode[] | undefined, id: number = 0): AnnoNode | null => {
    if (!nodeList) return null;

    for (let node of nodeList) {
      if (node.annoId === id) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        let foundNode = findAnnoNodeById(node.children, id);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  }

  const onAddChild = async (currentNode: AnnoNode, nodeToAdd: AnnoNodeTypeName | string, ordinal: number, isParent: boolean = false) => {
    let newChildren: any = [];
    if (currentNode.children) {
      newChildren = [...currentNode.children];
    }

    let newSubTree: AnnoNode = newAddedChildrenData[nodeToAdd.toLowerCase()];
    if (!newSubTree) {
      return;
    }

    newSubTree.children = [];
    newSubTree.nodeId = editorNode?.nodeId;
    newSubTree.ordinal = ordinal;

    if (isParent && contextMenuSubTree) {
      contextMenuSubTree.parentId = newSubTree.annoId;
      newSubTree.children?.push(contextMenuSubTree);
    }

    if (newSubTree.children && newSubTree.children.length === 0 && newSubTree.type !== 'ANNOTEXT') {
      await onAddChild(newSubTree, 'ANNOTEXT', 0);
    }

    currentNode.children = newChildren;

    currentNode.children?.forEach((childTree) => {
      let childPosition = childTree.ordinal;
      if (childPosition >= ordinal) {
        childTree.ordinal = childPosition + 1;
      }
    });

    newChildren.push(newSubTree);
    currentNode.children?.sort((a, b) => a.ordinal - b.ordinal);

    if (editorNode?.annotations) {
      let annoNodes = editorNode.annotations;

      try {
        const response = await saveEditorialContent(editorNode, EditorialType.ANNOTATION, annoNodes);
        dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
        dispatch({ type: ComponentManagementActionType.SUBTREE_CHANGED, nodeId: response.data.nodeId, hasChanges: true });
        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Node Added", severity: "success" } });
        dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: false });
        dispatch({ type: ComponentManagementActionType.ADD_EDIT_NODE_DIALOG_OPEN, open: false });
      } catch (error) {
        console.error(error);
      }
    }
  }

  function handleAddChildAnnoNode(nodeType: AnnoNodeTypeName) {
    if (contextMenuSubTree === undefined) {
      return;
    }

    let ordinal = 0;
    if (contextMenuSubTree.children && contextMenuSubTree.children.length > 0) {
      ordinal = contextMenuSubTree.children[contextMenuSubTree.children.length - 1].ordinal + 1;
    }

    onAddChild(contextMenuSubTree, nodeType, ordinal);
    setContextMenuOpen(false);
    setContextMenuSubTree(undefined);
  }

  function handleAddSiblingNode(ordinal: number, nodeType: AnnoNodeTypeName, direction: string) {
    if (contextMenuSubTree === undefined) return;

    let parentNode;

    if (!contextMenuSubTree.hasOwnProperty('parentId')) {
      parentNode = editorNode?.annotations;
    } else {
      parentNode = findAnnoNodeById(editorNode?.annotations?.children, contextMenuSubTree.parentId);
    }

    if (!parentNode) return;

    setCurrentAnnoNode(parentNode);
    setCurrentAnnoNodeType(nodeType);
    setCurrentAnnoNodeOrdinal(ordinal);
    setDirection(direction);
    setOpenAddSiblingDialog(true);
  }

  function handleAddParentNode(newParentNodeType: AnnoNodeTypeName) {
    if (contextMenuSubTree === undefined) return;

    let parentNode = findAnnoNodeById(editorNode?.annotations?.children, contextMenuSubTree.parentId);

    if (!parentNode) return;
    parentNode.children = parentNode.children?.filter(node => node.annoId !== contextMenuSubTree.annoId)
    onAddChild(parentNode, newParentNodeType, 0, true);
    setContextMenuOpen(false);
    setContextMenuSubTree(undefined);
  }

  function handleRemoveAnnoNodeAndChildren(node: AnnoNode) {
    setCurrentAnnoNode(node)
    setOpenRemoveNodeAndItsChildrenDialog(true);
  }

  const getSubtreeMenuItems = (selectedSubTree: AnnoNode, currentUser: User) => {
    if (!selectedSubTree) {
      return [];
    }
    let ret = [];

    const addMenuItem = (text: string, onClick: () => void, visible: boolean, classes: string = '') => {
      if (visible) ret.push(<MenuItem className={classes} onClick={onClick}>{text}</MenuItem>);
    }

    const isAdmin = currentUser.userRole === UserRole.ADMINISTRATOR;

    const isMenuOptionVisible = (type: string) => {
      const hierarchy = getLevelsHeirarchy()[type.toLowerCase()];
      if (!hierarchy) return false;

      return !selectedSubTree.children?.some(child => hierarchy.includes(child.type));
    };

    // Annotation Level
    if (!('type' in selectedSubTree)) {
      addMenuItem('Add Annotation Type', () => handleAddChildAnnoNode(AnnoNodeTypeName.HEADING), isMenuOptionVisible('ANNOTATION'));
      addMenuItem('Remove current node and children', () => handleRemoveAnnoNodeAndChildren(selectedSubTree), isAdmin && selectedSubTree?.children?.length > 0, classes.deleteMenuItem);
    }

    // Annotation Level Type
    if (selectedSubTree.type === 'HEADING') {
      addMenuItem('Add Annotation Type Before', () => handleAddSiblingNode(selectedSubTree.ordinal, AnnoNodeTypeName.HEADING, 'before'), true);
      addMenuItem('Add Annotation Type After', () => handleAddSiblingNode(selectedSubTree.ordinal + 1, AnnoNodeTypeName.HEADING, 'after'), true);
      addMenuItem('Add Division', () => handleAddChildAnnoNode(AnnoNodeTypeName.DIVISION), isMenuOptionVisible(selectedSubTree.type));
      addMenuItem('Add Content', () => handleAddChildAnnoNode(AnnoNodeTypeName.CONTENT), isMenuOptionVisible(selectedSubTree.type));
      addMenuItem('Remove current node and children', () => handleRemoveAnnoNodeAndChildren(selectedSubTree), isAdmin, classes.deleteMenuItem);
    }

    // Division Level
    if (selectedSubTree.type === 'DIVISION') {
      addMenuItem('Add Division Before', () => handleAddSiblingNode(selectedSubTree.ordinal, AnnoNodeTypeName.DIVISION, 'before'), true);
      addMenuItem('Add Division After', () => handleAddSiblingNode(selectedSubTree.ordinal + 1, AnnoNodeTypeName.DIVISION, 'after'), true);
      addMenuItem('Add Subdivision', () => handleAddChildAnnoNode(AnnoNodeTypeName.SUBDIVISION), isMenuOptionVisible(selectedSubTree.type));
      addMenuItem('Add Content', () => handleAddChildAnnoNode(AnnoNodeTypeName.CONTENT), isMenuOptionVisible(selectedSubTree.type));
      addMenuItem('Remove current node and children', () => handleRemoveAnnoNodeAndChildren(selectedSubTree), isAdmin, classes.deleteMenuItem);
    }

    // Subdivision Level
    if (selectedSubTree.type === 'SUBDIVISION') {
      addMenuItem('Add Subdivision Before', () => handleAddSiblingNode(selectedSubTree.ordinal, AnnoNodeTypeName.SUBDIVISION, 'before'), true);
      addMenuItem('Add Subdivision After', () => handleAddSiblingNode(selectedSubTree.ordinal + 1, AnnoNodeTypeName.SUBDIVISION, 'after'), true);
      addMenuItem('Add SubSubdivision', () => handleAddChildAnnoNode(AnnoNodeTypeName.SUBSUBDIVISION), isMenuOptionVisible(selectedSubTree.type));
      addMenuItem('Add Content', () => handleAddChildAnnoNode(AnnoNodeTypeName.CONTENT), isMenuOptionVisible(selectedSubTree.type));
      addMenuItem('Remove current node and children', () => handleRemoveAnnoNodeAndChildren(selectedSubTree), isAdmin, classes.deleteMenuItem);
    }

    // Subsubdivision Level
    if (selectedSubTree.type === 'SUBSUBDIVISION') {
      addMenuItem('Add SubSubdivision Before', () => handleAddSiblingNode(selectedSubTree.ordinal, AnnoNodeTypeName.SUBSUBDIVISION, 'before'), true);
      addMenuItem('Add SubSubdivision After', () => handleAddSiblingNode(selectedSubTree.ordinal + 1, AnnoNodeTypeName.SUBSUBDIVISION, 'after'), true);
      addMenuItem('Add Content', () => handleAddChildAnnoNode(AnnoNodeTypeName.CONTENT), isMenuOptionVisible(selectedSubTree.type));
      addMenuItem('Remove current node and children', () => handleRemoveAnnoNodeAndChildren(selectedSubTree), isAdmin, classes.deleteMenuItem);
    }

    // Content Level
    if (selectedSubTree.type === 'ANNOTEXT') {
      const parentNode = findAnnoNodeById(editorNode?.annotations?.children, selectedSubTree.parentId)

      addMenuItem('Create Division as a parent', () => handleAddParentNode(AnnoNodeTypeName.DIVISION), !(['SUBSUBDIVISION', 'SUBDIVISION', 'DIVISION'].includes(parentNode?.type ?? '')));
      addMenuItem('Create Subdivision as a parent', () => handleAddParentNode(AnnoNodeTypeName.SUBDIVISION), ['DIVISION'].includes(parentNode?.type ?? ''));
      addMenuItem('Create SubSubdivision as a parent', () => handleAddParentNode(AnnoNodeTypeName.SUBSUBDIVISION), ['SUBDIVISION'].includes(parentNode?.type ?? ''));
      addMenuItem('Remove node', () => handleRemoveAnnoNodeAndChildren(selectedSubTree), isAdmin, classes.deleteMenuItem);
    }

    if (ret.length === 0) {
      ret.push(<MenuItem>No Actions Available On This Node</MenuItem>);
    }

    return ret;
  }

  const onShowContextMenu = (event: React.MouseEvent, node: AnnoNode) => {
    if (!currentUser) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setContextMenuSubTree(node);
    setPosX(event.clientX - 2);
    setPosY(event.clientY - 4);
    setContextMenuOpen(true);
  }

  let selectedNodes: string[] = [];
  if (selectedNodeId || selectedNodeId === 0) {
    selectedNodes.push(selectedNodeId.toString());
  }
  if (contextMenuSubTree !== undefined && contextMenuSubTree.annoId && contextMenuSubTree.annoId !== selectedNodeId) {
    selectedNodes.push(contextMenuSubTree.annoId.toString());
  }

  async function removeAllChildren(currentAnnoNode: any) {
    for (const child of currentAnnoNode.children || []) {
      if (child && child.nodeId && child.annoId) {
        try {
          const response = await deleteAnnotationNodeById(child.nodeId, child.annoId);
          dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
          dispatch({ type: ComponentManagementActionType.SUBTREE_CHANGED, nodeId: response.data.nodeId, hasChanges: true });
          dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Node Removed", severity: "success" } });
          dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: false });
          dispatch({ type: ComponentManagementActionType.ADD_EDIT_NODE_DIALOG_OPEN, open: false });
        } catch (error) {
          console.error(error);
        }
      }
    }
  }

  useEffect(() => {
    if (!isSwitchModalOpen) {
      setExpandedAnnoNodes(toggledNodes);
    }
  }, [isSwitchModalOpen, toggledNodes]);

  const handleNodeLabelClick = (node: any) => {
    const cleanText = (text: string | undefined) =>
      text?.replace(/\u200B/g, '').replace(/^<p>([\s\S]*?)<\/p>$/, '$1');

    const currentText = cleanText(currentEditorTextForAnnotation);
    let nodeText = cleanText(currentClickedAnnoNode?.workingCopy);

    if (!('type' in node) && currentText === 'ANNOTATION') {
      nodeText = currentText;
    }

    if (currentText && nodeText && currentText.localeCompare(nodeText) !== 0) {
      setIsSwitchModalOpen(true);
      setOpenSwitchDialogue(true);
    } else {
      setIsSwitchModalOpen(false);
      onNodeClicked(node);
      dispatch({
        type: ComponentManagementActionType.EDITORIAL_STATES_CHANGED,
        editorialStates: editorialEditorStates,
        currentEditorTextForAnnotation: undefined,
      });
    }
    setCurrentClickedAnnoNode(node);
  }

  return (
    <>
      {currentUser && contextMenuOpen && posY !== null && posX !== null && (
        <Menu
          keepMounted
          open={contextMenuOpen}
          onClose={() => {
            setContextMenuOpen(false);
            setContextMenuSubTree(undefined);
          }}
          anchorReference="anchorPosition"
          anchorPosition={{ top: posY!, left: posX! }}
        >
          {contextMenuSubTree && getSubtreeMenuItems(contextMenuSubTree, currentUser)}
        </Menu>
      )}
      <Grid container direction='column' className={`${classes.root} flex-gap-1`}>
        <ConfirmationDialog
          open={openRemoveNodeAndItsChildrenDialog}
          title={`Remove current node and children`}
          contentText={`Are you sure you want to permanently remove all of the child nodes of this Annotation? This action cannot be reversed.`}
          handleNo={() => {
            setContextMenuOpen(false);
            setContextMenuSubTree(undefined);
            setOpenRemoveNodeAndItsChildrenDialog(false);
          }}
          handleYes={() => {
            if (currentAnnoNode && currentAnnoNode.nodeId && currentAnnoNode.annoId) {
              deleteAnnotationNodeById(currentAnnoNode.nodeId, currentAnnoNode.annoId).then(response => {
                dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
                dispatch({ type: ComponentManagementActionType.SUBTREE_CHANGED, nodeId: response.data.nodeId, hasChanges: true });
                dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Node Removed", severity: "success" } });
                dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: false });
                dispatch({ type: ComponentManagementActionType.ADD_EDIT_NODE_DIALOG_OPEN, open: false })
              }).catch(error => {
                console.error(error);
              });
            } else if (currentAnnoNode && !('type' in currentAnnoNode)) {
              removeAllChildren(currentAnnoNode);
            }
            setContextMenuOpen(false);
            setContextMenuSubTree(undefined);
            setOpenRemoveNodeAndItsChildrenDialog(false);
          }}
        />
        <ConfirmationDialog
          open={openAddSiblingDialog}
          title={`Add ${getAnnoNodeTypeNameText(currentAnnoNodeType!)}`}
          contentText={`Are you sure you would like to add a new ${getAnnoNodeTypeNameText(currentAnnoNodeType!)} ${direction} this ${getAnnoNodeTypeNameText(currentAnnoNodeType!)}?`}
          handleNo={() => {
            setContextMenuOpen(false);
            setContextMenuSubTree(undefined);
            setOpenAddSiblingDialog(false);
          }}
          handleYes={() => {
            if (currentAnnoNode) {
              onAddChild(currentAnnoNode, currentAnnoNodeType!, currentAnnoNodeOrdinal);
            }
            setContextMenuOpen(false);
            setContextMenuSubTree(undefined);
            setOpenAddSiblingDialog(false);
          }}
        />
        <ConfirmationDialog
          open={openSwitchDialogue}
          title={""}
          contentText={"You have made a change. Are you sure you want to change nodes? All changes will be lost."}
          handleNo={() => {
            setOpenSwitchDialogue(false)
            dispatch({
              type: ComponentManagementActionType.EDITORIAL_STATES_CHANGED,
              editorialStates: editorialEditorStates,
              currentEditorTextForAnnotation: currentEditorTextForAnnotation,
            });
          }}
          handleYes={() => {
            if (currentClickedAnnoNode) {
              onNodeClicked(currentClickedAnnoNode);
            }
            setExpandedAnnoNodes(toggledNodes);
            setOpenSwitchDialogue(false)
            dispatch({
              type: ComponentManagementActionType.EDITORIAL_STATES_CHANGED,
              editorialStates: editorialEditorStates,
              currentEditorTextForAnnotation: undefined,
            });
          }}
        />
        <Grid item>
          <TreeView
            selected={selectedNodes}
            expanded={expandedAnnoNodes.map(nodeId => nodeId.toString())}
            onNodeToggle={(_: ChangeEvent<{}>, nodeIds: string[]) => {
              let changedNodes = nodeIds.map(nodeId => parseInt(nodeId));
              setToggledNodes(changedNodes);
            }}
            defaultCollapseIcon={<MinusSquare />}
            defaultExpandIcon={<PlusSquare />}
          >
            <AnnotationTreeItemComponent parent={root} items={root.children || []} onNodeLabelClicked={handleNodeLabelClick} onContextMenu={onShowContextMenu} />
          </TreeView>
        </Grid>
      </Grid>
    </>
  );
};

export default AnnotationTreeView;
